import { ExternalLink, Clock3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import leagueData from "../../content/league";
import { formatDisplayDate, getLocale } from "../../utils/content";
import styles from "./LeagueHub.module.scss";

function scoreLabel(match) {
  if (match.status !== "result") {
    return match.time;
  }

  if (!match.isHome) {
    return `${match.scoreAgainst}:${match.scoreFor}`;
  }

  return `${match.scoreFor}:${match.scoreAgainst}`;
}

export default function LeagueHub() {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);

  const playedMatches = leagueData.matches.filter(
    (match) => match.status === "result"
  );
  const pendingMatches = leagueData.matches.filter(
    (match) => match.status === "pending"
  );
  const nextMatches = leagueData.matches.filter(
    (match) => match.status === "scheduled"
  );
  const playedMatchesNewestFirst = [...playedMatches].sort(
    (left, right) => new Date(right.date) - new Date(left.date)
  );

  return (
    <section className={styles.league}>
      <div className={styles.heroCard}>
        <span className={styles.eyebrow}>{t("league_page_title")}</span>
        <h1>{t("league_page_title")}</h1>
        <p>{t("league_intro")}</p>
        <div className={styles.meta}>
          <span>{t("league_source_note")}</span>
          <span>
            {t("league_last_updated", {
              date: formatDisplayDate(leagueData.lastUpdated, locale),
            })}
          </span>
        </div>
      </div>

      <div className={styles.layout}>
        <article className={styles.standingsCard}>
          <div className={styles.sectionHeading}>
            <div>
              <span className={styles.sectionLabel}>
                {t("league_standings_title")}
              </span>
              <h2>{t("league_standings_title")}</h2>
            </div>
            <a
              href={leagueData.officialPageUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.officialLink}
            >
              {t("button_open_official_site")}
              <ExternalLink size={16} />
            </a>
          </div>
          <div className={styles.standingsImageWrap}>
            <img
              src={leagueData.standingsImageUrl}
              alt={t("league_standings_caption")}
              className={styles.standingsImage}
            />
          </div>
          <p className={styles.caption}>{t("league_standings_caption")}</p>
        </article>

        <aside className={styles.nextCard}>
          <span className={styles.sectionLabel}>
            {t("league_next_match_title")}
          </span>
          <h2>{t("league_next_match_title")}</h2>
          <div className={styles.nextMatches}>
            {nextMatches.map((match) => (
              <div key={match.id} className={styles.nextMatch}>
                <div>
                  <strong>
                    {t("league_round", { round: match.round })}
                  </strong>
                  <p>
                    Warsaw Hockey Club vs. {match.opponent}
                  </p>
                </div>
                <div className={styles.kickoff}>
                  <Clock3 size={16} />
                  <span>
                    {formatDisplayDate(match.date, locale)}, {match.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {pendingMatches.length > 0 && (
        <aside className={styles.pendingCard}>
          <span className={styles.sectionLabel}>{t("league_pending_title")}</span>
          <h2>{t("league_pending_title")}</h2>
          <p className={styles.pendingNote}>{t("league_pending_note")}</p>
          <div className={styles.pendingList}>
            {pendingMatches.map((match) => (
              <div key={match.id} className={styles.pendingMatch}>
                <strong>{t("league_round", { round: match.round })}</strong>
                <span>
                  {formatDisplayDate(match.date, locale)}, {match.time}
                </span>
                <p>Warsaw Hockey Club vs. {match.opponent}</p>
              </div>
            ))}
          </div>
        </aside>
      )}

      <article className={styles.resultsCard}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionLabel}>
              {t("league_results_title")}
            </span>
            <h2>{t("league_results_title")}</h2>
          </div>
        </div>

        <div className={styles.resultsList}>
          {playedMatchesNewestFirst.map((match) => (
            <div key={match.id} className={styles.resultRow}>
              <div className={styles.resultMain}>
                <strong>{t("league_round", { round: match.round })}</strong>
                <p>
                  {match.isHome
                    ? `Warsaw Hockey Club vs. ${match.opponent}`
                    : `${match.opponent} vs. Warsaw Hockey Club`}
                </p>
              </div>
              <div className={styles.resultMeta}>
                <span>{formatDisplayDate(match.date, locale)}</span>
                <span>{match.isHome ? t("league_home") : t("league_away")}</span>
                <strong>{scoreLabel(match)}</strong>
              </div>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}
